import React, { useEffect, useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
} from "@mui/material";
import { useJobStore } from "../../store/Jobs";
import { JobDocumentEntityType } from "@/lib/supabase/supabaseTypes";
import { FormSectionWithAutosave } from "../dialog-components/form-section-with-autosave/form-section-with-autosave.component";
import { showNotification } from "../../store/Central/selectors";
import { Logger } from "@/lib/logger/Logger";

interface JobDocumentNoteDialogProps {
	open: boolean;
	onClose: () => void;
	jobDocument: JobDocumentEntityType;
}

export const JobDocumentNoteDialog: React.FC<JobDocumentNoteDialogProps> = ({
	open,
	onClose,
	jobDocument,
}) => {
	const { updateJobDocumentNote } = useJobStore((state) => ({
		updateJobDocumentNote: state.updateJobDocumentNote,
	}));

	const [isSaving, setIsSaving] = useState(false);
	const [isPendingChanges, setIsPendingChanges] = useState(false);
	const [formErrors, setFormErrors] = useState<Record<string, string | null>>(
		{}
	);
	const [note, setNote] = useState(jobDocument.note || "");

	// Track pending changes
	useEffect(() => {
		const hasChanges = note !== (jobDocument.note || "");
		setIsPendingChanges(hasChanges);
	}, [note, jobDocument.note]);

	// Reset form when dialog opens/closes or jobDocument changes
	useEffect(() => {
		if (open) {
			setNote(jobDocument.note || "");
			setIsPendingChanges(false);
		}
	}, [open, jobDocument.note]);

	const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setIsSaving(true);
		try {
			await updateJobDocumentNote(
				jobDocument.id,
				note || null,
				jobDocument.status
			);
			setIsPendingChanges(false);
		} catch (error) {
			Logger.error("Error saving note:", {}, error);
			setFormErrors({ note: "Fehler beim Speichern der Notiz" });
		} finally {
			setIsSaving(false);
		}
	};

	const handleClose = () => {
		if (!isPendingChanges) {
			setNote("");
			setIsPendingChanges(false);
			setFormErrors({});
			onClose();
		} else {
			showNotification({
				message: "Änderungen wurden noch nicht gespeichert!",
				type: "warning",
			});
		}
	};

	const handleDeleteNote = async () => {
		setNote("");
		setFormErrors({});
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle>Dokument Hinweis bearbeiten</DialogTitle>
			<DialogContent>
				<FormSectionWithAutosave
					onSubmit={onSubmit}
					formData={{ note: note }}
					isPendingChanges={isPendingChanges}
					formErrors={formErrors}
				>
					<div>
						<TextField
							value={note}
							onChange={(e) => setNote(e.target.value)}
							multiline
							rows={4}
							fullWidth
							label="Hinweis"
							placeholder="Geben Sie hier Ihren Hinweis ein..."
							variant="outlined"
						/>
					</div>
				</FormSectionWithAutosave>
			</DialogContent>
			<DialogActions>
				<div className="flex justify-start w-full">
					{note.length > 0 && (
						<Button onClick={handleDeleteNote}>
							Hinweis löschen
						</Button>
					)}
				</div>

				<div className="flex justify-end w-full">
					<Button
						onClick={handleClose}
						disabled={isSaving || isPendingChanges}
					>
						{isPendingChanges ? "Speichern..." : "Schliessen"}
					</Button>
				</div>
			</DialogActions>
		</Dialog>
	);
};
